<template>
  <div v-if="isVisible" class="overlay-container">
    <div class="overlay-background" @click="closeOverlay"></div>
    <div class="overlay-content">
      <div class="overlay-header">
        <h2>Filter Metering Points</h2>
        <button class="close-button" @click="closeOverlay">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="overlay-body">
        <div class="filter-section">
          <h3>{{ getFilterTitle(filterType) }}</h3>

          <form @submit.prevent="applyFilters">
            <div class="filter-options">
              <!-- HAS DEVICE filter options -->
              <template v-if="filterType === 'hasDevice'">
                <div class="filter-option">
                  <label for="deviceType">Device Type</label>
                  <select id="deviceType" v-model="localFilterOptions.deviceType">
                    <option value="">All Types</option>
                    <option value="mechanical">Mechanical</option>
                    <option value="dlms">DLMS</option>
                    <option value="lora">LoRa</option>
                    <option value="wbus">WBUS</option>
                    <option value="ctr">CTR</option>
                  </select>
                </div>

                <div class="filter-option">
                  <label for="lastReading">Last Reading</label>
                  <select id="lastReading" v-model="localFilterOptions.lastReading">
                    <option value="">Any time</option>
                    <option value="24h">Last 24 hours</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                  </select>
                </div>
              </template>

              <!-- NO DEVICE filter options -->
              <template v-if="filterType === 'noDevice'">
                <div class="filter-option">
                  <label for="moreThanDays">No device for more than</label>
                  <select id="moreThanDays" v-model="localFilterOptions.moreThanDays">
                    <option value="">Any time</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">1 year</option>
                  </select>
                </div>
              </template>

              <!-- COUNTRY filter options -->
              <template v-if="filterType === 'country'">
                <div class="filter-option">
                  <label for="specificCountry">Select Country</label>
                  <select id="specificCountry" v-model="localFilterOptions.specificCountry">
                    <option value="">All Countries</option>
                    <option value="usa">United States</option>
                    <option value="germany">Germany</option>
                    <option value="france">France</option>
                    <option value="uk">United Kingdom</option>
                    <option value="italy">Italy</option>
                    <option value="spain">Spain</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </template>
            </div>

            <div class="form-actions">
              <button type="button" class="cancel-button" @click="closeOverlay">Cancel</button>
              <button type="button" class="clear-button" @click="clearFilters">Clear</button>
              <button type="submit" class="apply-button">Apply</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MpidsFilterOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    filterType: {
      type: String,
      default: ''
    },
    initialFilterOptions: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localFilterOptions: {}
    };
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.initializeLocalOptions();
      }
    },
    initialFilterOptions: {
      handler() {
        this.initializeLocalOptions();
      },
      deep: true
    }
  },
  methods: {
    closeOverlay() {
      this.$emit('close');
    },
    initializeLocalOptions() {
      // Create a deep copy to avoid modifying props directly
      this.localFilterOptions = JSON.parse(JSON.stringify(this.initialFilterOptions || {}));
    },
    getFilterTitle(filterType) {
      switch (filterType) {
        case 'hasDevice':
          return 'Metering Points With Devices';
        case 'noDevice':
          return 'Metering Points Without Devices';
        case 'country':
          return 'Filter by Country';
        default:
          return 'Filter Options';
      }
    },
    clearFilters() {
      if (this.filterType === 'hasDevice') {
        this.localFilterOptions = {
          deviceType: '',
          lastReading: ''
        };
      } else if (this.filterType === 'noDevice') {
        this.localFilterOptions = {
          moreThanDays: ''
        };
      } else if (this.filterType === 'country') {
        this.localFilterOptions = {
          specificCountry: ''
        };
      }
    },
    applyFilters() {
      this.$emit('apply', {
        filterType: this.filterType,
        options: this.localFilterOptions
      });
      this.closeOverlay();
    }
  },
  created() {
    this.initializeLocalOptions();
  }
};
</script>

<style scoped>
.overlay-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
}

.overlay-content {
  position: relative;
  width: 90%;
  max-width: 500px;
  background-color: #1E1E1E;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.overlay-header {
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.overlay-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: white;
}

.close-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: white;
}

.overlay-body {
  padding: 24px;
}

.filter-section h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.filter-option {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-option label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.filter-option select,
.filter-option input {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 10px 12px;
  color: white;
  font-size: 14px;
  width: 100%;
}

.filter-option select:focus,
.filter-option input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.filter-option select option {
  background-color: #1E1E1E;
  color: white;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.cancel-button,
.clear-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 10px 16px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover,
.clear-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.apply-button {
  background-color: #4fc3f7;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  color: #121212;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.apply-button:hover {
  background-color: #81d4fa;
}
</style>